import { useState } from 'react';

const W = 640, H = 240, P = { t: 12, r: 12, b: 28, l: 40 };

/** Dependency-free responsive SVG multi-series line/area chart with keyboard-accessible tooltips. */
export default function LineChart({ labels, series }) {
  const [hover, setHover] = useState(null);
  const max = Math.max(1, ...series.flatMap((s) => s.values));
  const niceMax = Math.ceil(max / 4 / 10) * 10 * 4 || 4;
  const iw = W - P.l - P.r, ih = H - P.t - P.b;
  const x = (i) => P.l + (labels.length === 1 ? iw / 2 : (i / (labels.length - 1)) * iw);
  const y = (v) => P.t + ih - (v / niceMax) * ih;
  const ticks = [0, 1, 2, 3, 4].map((k) => (niceMax / 4) * k);

  return (
    <div className="cb-chart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Line chart: ${series.map((s) => s.name).join(' and ')} over time`} className="w-100">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={P.l} x2={W - P.r} y1={y(t)} y2={y(t)} stroke="#E5EAF2" />
            <text x={P.l - 6} y={y(t) + 4} textAnchor="end" fontSize="10" fill="#6B7280">{Math.round(t)}</text>
          </g>
        ))}
        {labels.map((l, i) => (
          <text key={l} x={x(i)} y={H - 8} textAnchor="middle" fontSize="10" fill="#6B7280">{l}</text>
        ))}
        {series.map((s, si) => {
          const line = s.values.map((v, i) => `${i ? 'L' : 'M'}${x(i)},${y(v)}`).join(' ');
          const area = `${line} L${x(labels.length - 1)},${y(0)} L${x(0)},${y(0)} Z`;
          return (
            <g key={s.name}>
              <path d={area} fill={s.color} opacity="0.08" />
              <path d={line} fill="none" stroke={s.color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
              {s.values.map((v, i) => (
                <circle key={i} cx={x(i)} cy={y(v)} r={hover === i ? 5 : 3} fill="#fff" stroke={s.color} strokeWidth="2" />
              ))}
              {si === 0 && null}
            </g>
          );
        })}
        {labels.map((l, i) => (
          <rect
            key={`hit-${l}`} x={x(i) - iw / labels.length / 2} y={P.t} width={iw / labels.length} height={ih}
            fill="transparent" tabIndex={0}
            aria-label={`${l}: ${series.map((s) => `${s.name} ${s.values[i]}`).join(', ')}`}
            onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
            onFocus={() => setHover(i)} onBlur={() => setHover(null)}
          />
        ))}
      </svg>
      {hover !== null && (
        <div className="cb-tooltip" role="status">
          <strong>{labels[hover]}</strong>
          {series.map((s) => (
            <div key={s.name}><span className="cb-dot" style={{ background: s.color }} />{s.name}: {s.values[hover]}</div>
          ))}
        </div>
      )}
      <ul className="list-inline small mt-2 mb-0">
        {series.map((s) => (
          <li key={s.name} className="list-inline-item me-3"><span className="cb-dot" style={{ background: s.color }} />{s.name}</li>
        ))}
      </ul>
    </div>
  );
}
